import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, admin } from '../lib/api.js';
import type { FoldersResult } from '../lib/types.js';
import './FolderPicker.css';

/**
 * Selettore della cartella condivisa. Naviga il drive invece di far digitare il
 * path a mano: i nomi reali sono lunghi e pieni di spazi (`NICOLINI DA MARZO
 * 2026_3128`), e un errore di battitura produce un cliente che non vede niente.
 */
export function FolderPicker({
  initialPath,
  onPick,
  onClose,
}: {
  initialPath: string;
  onPick: (path: string) => void;
  onClose: () => void;
}) {
  // Si apre sul genitore della cartella già scelta, non sulla radice.
  const [path, setPath] = useState(() => {
    const idx = initialPath.lastIndexOf('/');
    return idx === -1 ? '' : initialPath.slice(0, idx);
  });

  const folders = useQuery<FoldersResult>({
    queryKey: ['admin', 'folders', path],
    queryFn: () => admin.folders(path),
  });

  return (
    <div className="picker" role="dialog" aria-modal="true" aria-label="Scegli la cartella">
      <div className="picker__box card">
        <header className="picker__head">
          <h3>Scegli la cartella condivisa</h3>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <nav className="picker__crumbs" aria-label="Percorso">
          <button type="button" onClick={() => setPath('')}>
            Documents
          </button>
          {(folders.data?.breadcrumb ?? []).map((c) => (
            <span key={c.path}>
              <span className="picker__sep">/</span>
              <button type="button" onClick={() => setPath(c.path)}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>

        {folders.data?.suggeriti.length ? (
          <div className="picker__suggested">
            <span>Cartelle condivise qui:</span>
            {folders.data.suggeriti.map((s) => (
              <button key={s} type="button" className="btn btn--sm" onClick={() => onPick(s)}>
                {s.split('/').pop()}
              </button>
            ))}
          </div>
        ) : null}

        <div className="picker__list">
          {folders.isPending ? (
            <p className="picker__state">Caricamento…</p>
          ) : folders.isError ? (
            <div className="alert alert--error">
              {folders.error instanceof ApiError
                ? folders.error.message
                : 'Impossibile leggere le cartelle.'}
            </div>
          ) : folders.data.folders.length === 0 ? (
            <p className="picker__state">Nessuna sottocartella.</p>
          ) : (
            <ul>
              {folders.data.folders.map((f) => (
                <li key={f.path}>
                  <button
                    type="button"
                    className="picker__folder"
                    onClick={() => setPath(f.path)}
                    title={f.path}
                  >
                    <span className="picker__icon" aria-hidden="true">
                      📁
                    </span>
                    <span className="picker__name">{f.name}</span>
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => onPick(f.path)}>
                    Scegli
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="picker__foot">
          <code className="picker__current">{path || 'Documents (radice)'}</code>
          <button
            type="button"
            className="btn"
            disabled={path === ''}
            onClick={() => onPick(path)}
          >
            Usa questa cartella
          </button>
        </footer>
      </div>
    </div>
  );
}
